import { useState, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Save, Loader2 } from 'lucide-react'
import { curriculumBuilderApi, type UpdateDirectContentRequest } from '@/api/admin'

const LEVEL_LABELS: Record<string, string> = {
  curriculum: '커리큘럼',
  course: '코스',
  module: '모듈',
  lesson: '레슨',
}

const STATUS_OPTIONS = [
  { value: 'draft', label: '초안' },
  { value: 'ready', label: '준비됨' },
  { value: 'published', label: '발행됨' },
  { value: 'archived', label: '보관됨' },
]

interface ContentMetadataFormProps {
  content: {
    id: number
    title: string
    type: string
    description?: string | null
    bareTitle?: string | null
    shortDescription?: string | null
    estimatedDurationMinutes?: number | null
    thumbnailUrl?: string | null
    learningObjectives?: string | null
    concepts?: string | null
    qid?: string | null
    [key: string]: any
  }
  onUpdated: () => void
}

export function ContentMetadataForm({ content, onUpdated }: ContentMetadataFormProps) {
  const [form, setForm] = useState({
    title: content.title || '',
    bareTitle: content.bareTitle || '',
    description: content.description || '',
    shortDescription: content.shortDescription || '',
    estimatedDurationMinutes: content.estimatedDurationMinutes || '',
    thumbnailUrl: content.thumbnailUrl || '',
    learningObjectives: content.learningObjectives || '',
    concepts: content.concepts || '',
    status: content.status || 'draft',
  })

  // isDirectOrigin: only direct content can be edited
  // hierarchy API doesn't return origin, but lessons are always notion
  const isLesson = content.type === 'lesson'
  const isReadOnly = isLesson

  useEffect(() => {
    setForm({
      title: content.title || '',
      bareTitle: content.bareTitle || '',
      description: content.description || '',
      shortDescription: content.shortDescription || '',
      estimatedDurationMinutes: content.estimatedDurationMinutes || '',
      thumbnailUrl: content.thumbnailUrl || '',
      learningObjectives: content.learningObjectives || '',
      concepts: content.concepts || '',
      status: content.status || 'draft',
    })
  }, [content.id])

  const updateMutation = useMutation({
    mutationFn: (data: UpdateDirectContentRequest) => curriculumBuilderApi.updateContent(content.id, data),
    onSuccess: () => onUpdated(),
  })

  const handleSave = () => {
    const data: UpdateDirectContentRequest = {}
    if (form.title !== (content.title || '')) data.title = form.title
    if (form.bareTitle !== (content.bareTitle || '')) data.bareTitle = form.bareTitle
    if (form.description !== (content.description || '')) data.description = form.description
    if (form.shortDescription !== (content.shortDescription || '')) data.shortDescription = form.shortDescription
    if (form.learningObjectives !== (content.learningObjectives || '')) data.learningObjectives = form.learningObjectives
    if (form.concepts !== (content.concepts || '')) data.concepts = form.concepts
    if (form.thumbnailUrl !== (content.thumbnailUrl || '')) data.thumbnailUrl = form.thumbnailUrl
    if (form.status !== (content.status || 'draft')) data.status = form.status

    const mins = form.estimatedDurationMinutes === '' ? undefined : Number(form.estimatedDurationMinutes)
    if (mins !== (content.estimatedDurationMinutes || undefined)) data.estimatedDurationMinutes = mins

    if (Object.keys(data).length > 0) {
      updateMutation.mutate(data)
    }
  }

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">
            {LEVEL_LABELS[content.type] || content.type}
          </span>
          <h2 className="text-xl font-semibold text-slate-900 mt-1">{content.title}</h2>
          {content.qid && (
            <span className="text-xs text-slate-400 mt-1">QID: {content.qid}</span>
          )}
        </div>
        {!isReadOnly && (
          <button
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            저장
          </button>
        )}
      </div>

      {isReadOnly && (
        <div className="mb-4 px-3 py-2 bg-slate-100 text-slate-500 text-sm rounded-lg">
          레슨은 Notion에서 관리되는 콘텐츠이므로 여기서 수정할 수 없습니다.
        </div>
      )}

      {updateMutation.isSuccess && (
        <div className="mb-4 px-3 py-2 bg-green-50 text-green-700 text-sm rounded-lg">
          저장되었습니다.
        </div>
      )}

      {updateMutation.isError && (
        <div className="mb-4 px-3 py-2 bg-red-50 text-red-600 text-sm rounded-lg">
          저장에 실패했습니다.
        </div>
      )}

      <div className="space-y-4 bg-white rounded-xl border border-slate-200 p-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">제목</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            disabled={isReadOnly}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:bg-slate-50 disabled:text-slate-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">기본 제목 (Bare Title)</label>
          <input
            type="text"
            value={form.bareTitle}
            onChange={(e) => setForm({ ...form, bareTitle: e.target.value })}
            disabled={isReadOnly}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:bg-slate-50 disabled:text-slate-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">짧은 설명</label>
          <input
            type="text"
            value={form.shortDescription}
            onChange={(e) => setForm({ ...form, shortDescription: e.target.value })}
            disabled={isReadOnly}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:bg-slate-50 disabled:text-slate-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">설명</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            disabled={isReadOnly}
            rows={4}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none disabled:bg-slate-50 disabled:text-slate-500"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">예상 소요 시간 (분)</label>
            <input
              type="number"
              value={form.estimatedDurationMinutes}
              onChange={(e) => setForm({ ...form, estimatedDurationMinutes: e.target.value })}
              disabled={isReadOnly}
              placeholder="분"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:bg-slate-50 disabled:text-slate-500"
            />
          </div>
          {!isReadOnly && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">상태</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">학습 목표</label>
          <textarea
            value={form.learningObjectives}
            onChange={(e) => setForm({ ...form, learningObjectives: e.target.value })}
            disabled={isReadOnly}
            rows={3}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none disabled:bg-slate-50 disabled:text-slate-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">개념 (Concepts)</label>
          <textarea
            value={form.concepts}
            onChange={(e) => setForm({ ...form, concepts: e.target.value })}
            disabled={isReadOnly}
            rows={2}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none disabled:bg-slate-50 disabled:text-slate-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">썸네일 URL</label>
          <input
            type="text"
            value={form.thumbnailUrl}
            onChange={(e) => setForm({ ...form, thumbnailUrl: e.target.value })}
            disabled={isReadOnly}
            placeholder="https://..."
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:bg-slate-50 disabled:text-slate-500"
          />
        </div>
      </div>
    </div>
  )
}
